"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Why hire an outsourced revenue consultant over an in-house manager?",
  answer: "An outsourced revenue consultant provides senior-level expertise, advanced analytics, and cross-market intelligence at a fraction of the cost of a full-time executive. You eliminate recruitment overhead, payroll taxes, and training lag while gaining immediate access to proven OTA distribution systems and yield management strategies tested across diverse hospitality portfolios."
}, {
  id: "item-2",
  question: "Which platforms and PMSs do you support?",
  answer: "We work across all primary Property Management Systems and Channel Managers in the hospitality industry. Supported platforms include Opera (Oracle), Cloudbeds, Hotelogix, eZee Absolute, WebRezPro, SiteMinder, RateGain, STAAH, and leading Revenue Management Systems (RMS) such as IDeaS and Duetto."
}, {
  id: "item-3",
  question: "How fast do we see measurable ADR and RevPAR gains?",
  answer: "Initial optimization adjustments — including OTA rate parity cleanup, inventory reallocation, and baseline channel restructuring — typically deliver visible booking pickup within the first 14 to 30 days. Sustained double-digit improvements in ADR and RevPAR compound over 60 to 90 days as dynamic pricing models calibrate to your market demand cycles."
}, {
  id: "item-4",
  question: "What does the initial hotel revenue audit cover?",
  answer: "Our comprehensive audit evaluates historical booking trends, compset pricing positioning, OTA visibility scores, channel margin leakages, and your existing tech stack efficiency. You receive a clear, actionable roadmap with prioritized revenue opportunities."
}];
const highlights = [{
  title: "Tailored yield models",
  description: "Customized for independent hotels, boutique resorts, and multi-property groups."
}, {
  title: "Zero tech lock-in",
  description: "We optimize your current tech stack rather than forcing expensive new software."
}, {
  title: "Direct principal access",
  description: "Collaborate directly with Sunil Singh on weekly rate strategies and market reviews."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
  return <section id="faqaccordion" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-t border-border">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLdData)
    }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Heading & Context */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between h-full space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="bg-accent text-accent-foreground border-border px-3 py-1 font-mono uppercase text-xs tracking-wider">
                  <HelpCircle className="size-3.5 text-secondary" />
                  <span>Frequently asked questions</span>
                </Badge>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-tight">
                Answers to common revenue questions
              </h2>

              <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed">
                Clear facts on outsourcing revenue management, platform compatibility, and expected returns for independent and group properties.
              </p>
            </div>

            {/* Value Highlights */}
            <div className="space-y-4 pt-4 border-t border-border">
              {highlights.map((highlight, index) => <div key={index} data-index={index} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {highlight.title}
                    </h3>
                    <p className="text-xs text-foreground/70 mt-0.5">
                      {highlight.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Quick Contact Card */}
            <div className="p-6 bg-card rounded-xl border border-border shadow-sm">
              <h3 className="text-base font-semibold text-card-foreground">
                Have a unique property requirement?
              </h3>
              <p className="mt-2 text-sm text-card-foreground/80">
                Book a consultation with Sunil Singh to discuss your property compset and distribution challenges.
              </p>
              <div className="mt-4">
                <Button asChild variant="secondary" className="w-full sm:w-auto rounded-full">
                  <Link to="#" className="inline-flex items-center gap-2">
                    <span>Schedule a consultation</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Accordion List */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-7 bg-card p-6 sm:p-8 rounded-xl border border-border shadow-sm">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqList.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border border-border/80 rounded-xl px-5 py-1 bg-background/50 data-[state=open]:border-secondary/50 data-[state=open]:bg-accent/10 transition-[border-color,background-color] duration-200">
                  <AccordionTrigger className="text-left font-serif font-semibold text-base sm:text-lg text-foreground hover:no-underline py-4">
                    <span className="flex items-center gap-3">
                      <span className="font-mono text-xs text-secondary shrink-0 font-normal">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-foreground/80 leading-relaxed pt-2 pb-5 border-t border-border/40 mt-1">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-foreground/70 font-mono">
                Looking for detailed tech stack implementation?
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="#" className="inline-flex items-center gap-1.5 text-xs font-semibold">
                  <span>Explore services</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}